import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobCluster } from '~/shared/data-contract/job-cluster';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { updateFamiliesWithClustersCache } from './use-families-with-clusters';

export type UseDeleteJobClustersParams = {
    readonly jobClusters: NonEmptyArray<JobCluster>;
};

export type UseDeleteJobClustersResult = UseMutationResult<unknown, Error, UseDeleteJobClustersParams>;

export const useDeleteJobClusters = (): UseDeleteJobClustersResult => {
    const { deleteJobClusters } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobClusters }: UseDeleteJobClustersParams) => {
            const result = await deleteJobClusters(jobClusters);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobClusters[0];
            const idsToDelete = new Set(params.jobClusters.map((cluster) => cluster.id));

            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });

            updateFamiliesWithClustersCache(queryClient, architectureId, (old) =>
                old.map((family) => ({
                    ...family,
                    clusters: family.clusters.filter((cluster) => !idsToDelete.has(cluster.id)),
                })),
            );
        },
        onSuccess: (_, { jobClusters }) => {
            const { architectureId } = jobClusters[0];
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });
        },
        throwOnError: false,
    });
};
